/**
 * A7Y-5556 — shared shapes for the AI APIs admin section (epic A7Y-5555).
 *
 * LLM API spend only: the Claude and Codex subscription accounts (AI Quotas)
 * are out of scope. Every figure is read from one BigQuery table,
 * `a7y_data.ai_api_usage_daily` (declared in
 * `scripts/bq-schemas/ai_api_usage_daily.schema.json`), whose row shape is
 * {@link AiApiUsageDailyRow}. The a7y-functions provider wrappers emit one
 * {@link LlmCallLogEvent} per provider response into `server_log`, which the
 * nightly snapshot rolls up into `source = 'call_log'` rows.
 *
 * Pure and dependency-free, so a7y-api, a7y-admin and a7y-functions compile
 * against one shape.
 */

export type AiApiProvider = 'anthropic' | 'openai' | 'gemini' | 'perplexity';

export const AI_API_PROVIDER_IDS: readonly AiApiProvider[] = [
  'anthropic',
  'openai',
  'gemini',
  'perplexity',
];

export function isAiApiProvider(value: unknown): value is AiApiProvider {
  return typeof value === 'string' && (AI_API_PROVIDER_IDS as readonly string[]).includes(value);
}

export interface AiApiProviderInfo {
  label: string;
  /** The provider console page that shows usage. */
  dashboardUrl: string;
  /** Where the provider bills, when that is a different page. */
  billingUrl?: string;
  /** Chart hue; taken from `a7y-admin/src/pages/finance/palette.ts`. */
  colorHex: string;
  /** Whether the provider's usage API reports per-key figures. */
  hasKeyDimension: boolean;
  /** Secret names that hold this provider's API keys. */
  keySecretNames: string[];
}

export const AI_API_PROVIDERS: Record<AiApiProvider, AiApiProviderInfo> = {
  anthropic: {
    label: 'Anthropic',
    dashboardUrl: 'https://console.anthropic.com/settings/usage',
    colorHex: '#f59e0b', // amber-500
    hasKeyDimension: true,
    keySecretNames: [
      'ANTHROPIC_API_KEY',
      'ANTHROPIC_API_KEY_STAGING',
      'ANTHROPIC_API_KEY_CI_PRISM',
      'ANTHROPIC_API_KEY_CI_EXPERT_EYE',
      'ANTHROPIC_API_KEY_CI_CLAIM_SWEEP',
      'ANTHROPIC_API_KEY_CI_GTM',
      'ANTHROPIC_API_KEY_CI_SAAS_LEDGER',
      'ANTHROPIC_API_KEY_CI_DEPLOY',
      'ANTHROPIC_API_KEY_AQUIX',
      'ANTHROPIC_API_KEY_SCRIPTS',
    ],
  },
  openai: {
    label: 'OpenAI',
    dashboardUrl: 'https://platform.openai.com/settings/organization/usage',
    colorHex: '#10b981', // emerald-500
    hasKeyDimension: true,
    keySecretNames: ['OPENAI_KEY', 'OPENAI_KEY_STAGING', 'OPENAI_KEY_SCRIPTS'],
  },
  gemini: {
    label: 'Gemini',
    // Not confirmed in a browser (A7Y-5556): the page redirects to sign-in.
    dashboardUrl: 'https://aistudio.google.com/usage',
    billingUrl: 'https://console.cloud.google.com/billing?project=apptonomy-gcp',
    colorHex: '#3b82f6', // blue-500
    hasKeyDimension: false,
    keySecretNames: ['GEMINI_API_KEY'],
  },
  perplexity: {
    label: 'Perplexity',
    dashboardUrl: 'https://www.perplexity.ai/settings/api',
    billingUrl: 'https://console.perplexity.ai/project/billing',
    colorHex: '#06b6d4', // cyan-500
    hasKeyDimension: false,
    keySecretNames: ['PERPLEXITY_API_KEY'],
  },
};

/**
 * `provider_usage`: tokens from the provider API, cost from our rate table.
 * `provider_cost`: billed dollars from the provider, no tokens.
 * `call_log`: the roll-up of our own `llm_call` events.
 */
export type AiApiUsageSource = 'provider_usage' | 'provider_cost' | 'call_log';
export type AiApiCostBasis = 'rate_table' | 'billed';

/** One `a7y_data.ai_api_usage_daily` row; field per column, so it is also the insert shape. */
export interface AiApiUsageDailyRow {
  /** UTC usage day, `YYYY-MM-DD`. */
  day: string;
  provider: AiApiProvider;
  source: AiApiUsageSource;
  model: string | null;
  api_key_id: string | null;
  api_key_hint: string | null;
  key_label: string | null;
  env: string | null;
  service_tier: string | null;
  requests: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  cost_usd: number;
  cost_basis: AiApiCostBasis;
  /** Pre-stringified JSON object. */
  detail: string | null;
  /** ISO timestamp; the dedupe order key. */
  pulled_at: string;
  schema_version: string;
}

/** The structured `llm_call` event every provider wrapper logs, one per provider response. */
export interface LlmCallLogEvent {
  event: 'llm_call';
  provider: AiApiProvider;
  /** The model the request was sent to. */
  model: string;
  /** The model the caller asked for (differs only under the harness override). */
  requested_model: string;
  env: string;
  key_label: string;
  /** `redactApiKey` of the key used; never the key. */
  key_hint: string;
  /** The wrapper function that made the call. */
  entry_point: string;
  /** The first stack frame outside the provider clients. */
  call_site: string;
  /** The registry task, when the call came through the dispatcher. */
  task: string | null;
  audit_id: string | null;
  /** Anthropic `standard` / `batch`; null elsewhere. */
  service_tier: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  /** 0 on an AI Gateway cache hit, otherwise 1. */
  requests: number;
  cache_hit: boolean;
  cost_usd: number;
  latency_ms: number;
  error: string | null;
  ts: string;
}

/** First 8 characters, an ellipsis and the last 4, for a key of 16 characters or more; otherwise `redacted`. */
export function redactApiKey(value: string): string {
  if (typeof value !== 'string' || value.length < 16) return 'redacted';
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

// =============================================================================
// a7y-api response shapes for the admin section
// =============================================================================

export interface AiApiTokenTotals {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  requests: number;
}

export interface AiApisOverviewResponse {
  generatedAt: string;
  months: string[];
  providers: Array<{
    provider: AiApiProvider;
    monthToDate: AiApiTokenTotals & { costUsd: number; costBasis: AiApiCostBasis | 'none' };
    monthly: Array<
      { month: string; costUsd: number; costBasis: AiApiCostBasis | 'none' } & AiApiTokenTotals
    >;
    ledgerTopUpsUsdByMonth?: Record<string, number>;
  }>;
}

export interface AiApiProviderMonthsResponse {
  provider: AiApiProvider;
  months: Array<
    {
      month: string;
      costUsd: number;
      costBasis: AiApiCostBasis | 'none';
      sources: AiApiUsageSource[];
    } & AiApiTokenTotals
  >;
}

export interface AiApiProviderMonthDetailResponse {
  provider: AiApiProvider;
  month: string;
  models: Array<
    {
      model: string;
      source: AiApiUsageSource;
      costBasis: AiApiCostBasis;
      costUsd: number;
    } & AiApiTokenTotals
  >;
  keys: Array<
    {
      apiKeyId: string | null;
      apiKeyHint: string | null;
      keyLabel: string | null;
      costUsd: number;
      share: number;
    } & AiApiTokenTotals
  >;
  crossCheck: Array<{
    env: string;
    callLogCostUsd: number;
    callLogRequests: number;
    providerCostUsd: number | null;
  }>;
  unmappedSkus: Array<{ sku: string; costUsd: number }>;
  ledgerRows: Array<{ date: string; description: string; amountUsd: number; sheetUrl: string }>;
}

export interface AiApiCatalogPricing {
  inputPerMillion?: number;
  cachedInputPerMillion?: number;
  cacheWritePerMillion?: number;
  outputPerMillion?: number;
  perRequest?: number;
}

export interface AiApiCatalogModel {
  model: string;
  displayName: string;
  generation: string;
  useCase: string;
  contextWindow?: number;
  pricing: AiApiCatalogPricing | null;
  /** `registry`: priced from our rate tables; `catalog`: list price as read on `readAt`. */
  pricingSource: 'registry' | 'catalog';
  /** Whether a registry task routes to this model. */
  inUse: boolean;
  /** The registry tasks that name this model. */
  tasks: string[];
}

export interface AiApiModelCatalogResponse {
  /** The day the pricing pages were read, `YYYY-MM-DD`. */
  readAt: string;
  providers: Record<
    AiApiProvider,
    { sourceUrl: string; batchMultiplier?: number; models: AiApiCatalogModel[] }
  >;
}
